import React, { createContext, useContext, useState } from 'react';
import { UserStatus, UserRole } from 'src/admin/users/types';
import { useCreateUser } from '../hooks/useCreateUser';
import useUsers from '../hooks/useUsers';
import { useUpdateUser } from '../hooks/useUpdateUser';
import useCurrentUser from '../hooks/useCurrentUser';

type Flow = 'create-user' | 'edit-user' | null;

export type CreateUserData = {
  emailAddress: string;
  firstName: string;
  lastName: string;
  userStatus: UserStatus;
  userRole: UserRole;
  isAdmin?: boolean;
};

interface UsersContextType {
  flow: Flow;
  flowId?: string;
  isLoading: boolean;
  error: Error | null;
  startUserCreation: () => void;
  editUser: (id: string, data: CreateUserData) => Promise<void>;
  createUser: (data: CreateUserData) => Promise<void>;
  close: () => void;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export function UsersProvider({ children }: { children: React.ReactNode }) {
  const [flow, setFlow] = useState<Flow>(null);
  const [flowId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { createUser, error: createUserError } = useCreateUser();
  const { updateUser } = useUpdateUser();
  const { isSystemAdmin } = useCurrentUser();
  const { refetch: refetchUsers } = useUsers([], !isSystemAdmin);
  // const navigate = useNavigate();

  const startUserCreation = () => {
    setFlow('create-user');
    setError(null);
  };

  const close = () => {
    setFlow(null);
    setError(null);
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      setIsLoading(true);
      setError(null);
      await createUser(data);
      setFlow(null);
      refetchUsers();
      // navigate(`/users/${user.data.id}`);
    } catch (error) {
      setError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = async (id: string, data: CreateUserData) => {
    try {
      setIsLoading(true);
      setError(null);
      await updateUser(id, data);
      setFlow(null);
      refetchUsers();
      // navigate(`/users/${user.data.id}`);
    } catch (error) {
      setError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UsersContext
      value={{
        flow,
        flowId,
        isLoading,
        error: error || createUserError,
        startUserCreation,
        editUser: handleEditUser,
        createUser: handleCreateUser,
        close,
      }}
    >
      {children}
    </UsersContext>
  );
}

export function useUsersContext() {
  const context = useContext(UsersContext);
  if (context === undefined) {
    throw new Error('useUsersContext must be used within a UsersProvider');
  }
  return context;
}
